const DOJO_SDK_URL = 'https://cdn.dojo.tech/payments/v1/client.js';

let cached: Promise<void> | undefined = undefined;

/**
 * Loads the Dojo Payments web SDK on demand — only merchants configured with
 * Dojo pay the cost of the CDN request. The bundle assigns itself to
 * `window.Dojo`, which is what the rest of this folder consumes.
 */
export function loadDojo(): Promise<void> {
    if (window.Dojo) {
        return Promise.resolve();
    }

    const existing = cached;
    if (existing) {
        return existing;
    }

    const promise = new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        script.src = DOJO_SDK_URL;
        script.async = true;

        const forget = (): void => {
            cleanup();
            // Drop the cached rejection so the next caller — the Retry button —
            // gets a fresh attempt rather than the previous failure.
            cached = undefined;
            script.remove();
        };

        const onScriptLoad = (): void => {
            if (!window.Dojo) {
                forget();
                reject(new Error(`Script ${DOJO_SDK_URL} loaded without defining window.Dojo`));
                return;
            }
            cleanup();
            resolve();
        };

        const onScriptError = (): void => {
            forget();
            reject(new Error(`Unable to load script ${DOJO_SDK_URL}`));
        };

        script.addEventListener('load', onScriptLoad);
        script.addEventListener('error', onScriptError);
        document.body.appendChild(script);

        function cleanup(): void {
            script.removeEventListener('load', onScriptLoad);
            script.removeEventListener('error', onScriptError);
        }
    });
    cached = promise;

    return promise;
}
